import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getProduct,
  getProducts,
  getProductsList,
  createProduct,
  updateProduct,
  deleteProduct,
} from '~/api/products';
import type { ArgsGetProducts, IProduct } from '~/types/products';

const useGetProducts = (params: ArgsGetProducts) => useQuery({
  queryKey: ['products', params],
  queryFn: async () => getProducts(params),
  keepPreviousData: true,
});

const useGetProductsList = () => useQuery({
  queryKey: ['products-list'],
  queryFn: async () => getProductsList(),
});

const useGetProduct = (id: string) => useQuery({
  queryKey: ['product', id],
  queryFn: async () => getProduct(id),
  enabled: !!id,
});

const useCreateProduct = (successMessage = 'Produit créé') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<IProduct>) => createProduct(payload),
    onSuccess: async (data) => {
      toast.success(successMessage);
      await queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      if (data?.product?._id) {
        queryClient.setQueryData(['product', data.product._id], data.product);
      }
    },
  });
};

const useUpdateProduct = (successMessage = 'Les modifications ont été prises en compte') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<IProduct>) => updateProduct(payload),
    onSuccess: async (data) => {
      toast.success(successMessage);
      await queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      await queryClient.invalidateQueries({ queryKey: ['products-list'] });
      if (data?.product?._id) {
        queryClient.setQueryData(['product', data.product._id], data.product);
      }
    },
  });
};

const useDeleteProduct = (successMessage = 'Produit supprimé') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => deleteProduct(id),
    onSuccess: async () => {
      toast.success(successMessage);
      await queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
    },
  });
};

export {
  useGetProducts,
  useGetProductsList,
  useGetProduct,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
};
